import { GeometryTypeEnum } from '../enums/geometry-type-enum';
import { LayerTypeEnum } from '../enums/layer-type-enum';
import { LayerListingValueObject } from '../value-objects/layer-listing-value-object';

export class LayerEntity {
  id!: string
  name!: string
  type!: LayerTypeEnum
  visible: boolean = false
  geometryType?: GeometryTypeEnum
  referenceSource?: string
  hasProxy?: boolean
  opacity: number = 1
  url?: string | null
  definitionQuery?: string | null
  definitionQueryWithLabel?: boolean
  definitionGeometry?: any
  filterWithGeometry?: boolean
  categoryField?: string | null
  disabled?: boolean
  minScale: number = 0
  maxScale: number = 0
  layers?: LayerEntity[]
  abbreviation?: string
  queryFields?: string[]
  fieldQuery?: string | null
  /** true si fieldQuery se filtra por nombre (NOMBDEP) y no por código ubigeo. */
  fieldQueryWithLabel?: boolean
  metadata?: any
  displayField?: string
  popupEnabled: boolean = true
  labelsVisible: boolean = true
  /** PoC: renderer CIM con animación "MoveAlongLine" (SDK >= 5.1, beta) para capas de línea (p. ej. flujo en ríos/tramos). Solo MapView (2D). */
  animatedFlow?: boolean
  /** Agrupa puntos con featureReduction cluster (SDK): mejora legibilidad a escala nacional. Solo FeatureLayers de puntos. */
  clusterPoints?: boolean
  /**
   * Pinta la capa como mapa de calor (`HeatmapRenderer`) en lugar de símbolos individuales:
   * para capas de puntos densos y anónimos donde la pregunta es "¿dónde se concentra?".
   * Solo FeatureServer de puntos y mapas 2D; en este modo no hay popup por feature ni
   * etiquetas, y es excluyente con `clusterPoints` (gana el heatmap).
   */
  heatmapPoints?: boolean
  /** Campo numérico que pondera la densidad del heatmap (p. ej. "VIV2017"); sin campo, cada punto pesa 1. */
  heatmapField?: string | null
  /**
   * Escala máxima (denominador 1:N) a la que se mantiene el agrupamiento de `clusterPoints`:
   * p. ej. 100000 = agrupado a 1:100 000 o más alejado, y puntos individuales al acercarse más.
   * 0/undefined = agrupar a cualquier escala (comportamiento por defecto del SDK).
   */
  clusterMaxScale?: number
  /**
   * Campos que la etiqueta y el popup resumen de cada cluster (`FeatureReductionCluster.fields`,
   * SDK >= 4.25): suma de población, habitantes atendidos, estado predominante de la zona, etc.
   */
  clusterAggregates?: LayerClusterAggregate[]
  /**
   * Campo de texto con el que se etiqueta cada feature cuando la capa NO está agrupada
   * (p. ej. "QUEBRADA" en las barreras dinámicas); activa `labelingInfo` en el cliente.
   */
  labelField?: string | null
  /** Escala más alejada (denominador 1:N) a la que se muestran las etiquetas de `labelField`. 0 = sin límite. */
  labelMinScale?: number
  /**
   * Escala más cercana (denominador 1:N) a la que se muestran las etiquetas de `labelField`.
   * 0 = sin límite (la etiqueta persiste al máximo zoom). Contraparte de `labelMinScale` en el
   * caso de `clusterMaxScale`, para poder acotar la etiqueta por ambos extremos.
   */
  labelMaxScale?: number
  proxyUrl?: string | null
  /** Propiedades de listado/visibilidad por contexto (endpoint /layers/monitoring). */
  listing?: LayerListingValueObject
  instance?: any
  constructor(init?: Partial<LayerEntity>) {
    Object.assign(this, init);
  }
  /**
   * true si la capa se dibuja con un renderer de **cliente** en lugar del que declara el
   * servicio (densidad, agrupamiento o flujo animado).
   *
   * Importa porque la leyenda del servicio describe el estilo del servidor, que en estas capas
   * no es lo que se ve en el mapa: preguntar por ella mostraría en el TOC una simbología
   * equivocada. Fuente única de verdad para decidir si la leyenda hay que sintetizar en el
   * cliente en vez de pedirla al servicio.
   */
  get hasClientRender(): boolean {
    return !!this.heatmapPoints || !!this.clusterPoints || !!this.animatedFlow
  }
  setValue(value: any): void {
    if (this.filterWithGeometry) {
      this.definitionGeometry = value ?? null;
      return;
    }
    if (this.definitionQuery === null || this.definitionQuery === undefined || value === null || value === undefined || value === '') {
      this.definitionQuery = null;
      return;
    }
    this.definitionQuery = this.definitionQuery?.replace('{0}', value);
  }
}

/** Resumen de un campo del servicio aplicado a los puntos que caen dentro de cada cluster. */
export interface LayerClusterAggregate {
  /** Campo del servicio que se resume; se omite solo para `statisticType: 'count'`. */
  field?: string
  /** Operación de resumen: `sum`, `avg`, `min`, `max`, `mode` (valor predominante) o `count`. */
  statisticType?: 'sum' | 'avg' | 'min' | 'max' | 'count' | 'mode'
  /** Nombre legible del resumen en el popup y la leyenda; por defecto se deriva del campo. */
  alias?: string
  /** Texto que precede al valor en la etiqueta del cluster (p. ej. "POB"). */
  labelPrefix?: string
  /** Unidad que sigue al valor en la etiqueta del cluster (p. ej. "hab."). */
  unit?: string
  /** false = el resumen se muestra solo en el popup, no en la etiqueta del cluster. */
  inLabel?: boolean
  /** false = el campo es de texto (p. ej. el estado predominante) y no se formatea como número. */
  numeric?: boolean
}

